import { useState } from "react";
import { TextField, Checkbox } from "@lannister/duplo";

import { ReactComponent as CardIcon } from "../../assets/icons/card.svg";
import { ReactComponent as CalendarIcon } from "../../assets/icons/calendar.svg";
import { ReactComponent as EyeOffIcon } from "../../assets/icons/eye-slash.svg";
import { ReactComponent as EyeOnIcon } from "../../assets/icons/eye.svg";

import UTILS from "../../common/utils";
import { ValidateNumeric, FormatExpiryDate, FormatCardNumber } from "../Checkout/utils";

const FIELDS = {
    first_name:     { key: "first_name",     label: "First Name",  maxLength: 50 },
    last_name:      { key: "last_name",      label: "Last Name",   maxLength: 50 },
    payment_number: { key: "payment_number", label: "Card Number", maxLength: 19 },
    expiry_date:    { key: "expiry_date",    label: "MM/YY",       maxLength: 5  },
    cvv:            { key: "cvv",            label: "CVV",         maxLength: 3  },
};

const CardDetailsForm = ({ handleChange, isSaveCardOptionEnabled, isCVVRequired = true }) => {
  const [showCVV, setShowCVV] = useState(false);
  const [shouldSaveCard, setShouldSaveCard] = useState(false);
  const [cardData, setCardData] = useState({});
  const [touched, setTouched] = useState({});

  const handleBlur = (fieldKey) => {
      setTouched(prev => ({ ...prev, [fieldKey]: true }));
  }

  const toggleCvvVisibility = () => {
    setShowCVV((prev) => !prev);
  }

  const onFieldChange = (field, value) => {
    setCardData(prev => ({ ...prev, [field]: value }));
    handleChange(field, value);
  }

  const onChangeShouldSaveCard = (value) => {
    setShouldSaveCard(value);
    onFieldChange("should_save_payment_method", value);
  }

  const getErrorMsg = (field) => {
    if (!touched[field.key]) return undefined;
    if (!cardData[field.key]) 
        return `${field.label} is required`;
    return undefined;
  }

  const getExpiryDateErrorMsg = (date) => {
    if (!touched["expiry_date"]) return undefined;
    if (!date) 
        return `Expiry Date is required`;

    if (date && UTILS.isExpiryValid(date) === false) {
      return "Wrong Exp. Date";
    }
    return undefined;
  }

  return (
        <div style={styles.card}>
          <div style={styles.row}>
              <div style={styles.field}>
                  <TextField
                    label={FIELDS.first_name.label}
                    value={cardData.first_name}
                    onChange={e => onFieldChange(FIELDS.first_name.key, e.target.value)}
                    maxLength={FIELDS.first_name.maxLength}
                    onBlur={() => handleBlur(FIELDS.first_name.key)}
                    errorMessage={getErrorMsg(FIELDS.first_name)}
                  />
              </div>
              <div style={styles.field}>
                  <TextField
                    label={FIELDS.last_name.label}
                    value={cardData.last_name}
                    onChange={e => onFieldChange(FIELDS.last_name.key, e.target.value)}
                    maxLength={50}
                    onBlur={() => handleBlur(FIELDS.last_name.key)}
                    errorMessage={getErrorMsg(FIELDS.last_name)}
                  />
              </div>
          </div>

          <TextField
              label={FIELDS.payment_number.label}
              value={cardData.payment_number}
              onChange={e => onFieldChange(FIELDS.payment_number.key, e.target.value)}
              rightIcon={<CardIcon />}
              maxLength={19}
              onBlur={() => handleBlur(FIELDS.payment_number.key)}
              errorMessage={getErrorMsg(FIELDS.payment_number)}
              onInput={
                (event) => {
                  ValidateNumeric(event);
                  FormatCardNumber(event);
                }}
          />

          <div style={styles.row}>
              <div style={styles.field}>
                  <TextField
                    label={FIELDS.expiry_date.label}
                    value={cardData.expiry_date}
                    onChange={e => onFieldChange(FIELDS.expiry_date.key, e.target.value)}
                    rightIcon={<CalendarIcon />}
                    maxLength={5}
                    onInput={
                      (event) => {
                        ValidateNumeric(event);
                        FormatExpiryDate(event, cardData.expiry_date);
                      }}
                    onBlur={() => handleBlur(FIELDS.expiry_date.key)}
                    errorMessage={getExpiryDateErrorMsg(cardData.expiry_date)}
                  />
                  {isSaveCardOptionEnabled &&
                    <Checkbox
                      label="Save Card"
                      checked={shouldSaveCard}
                      onChange={(value) => onChangeShouldSaveCard(value)} />}
              </div>
              {isCVVRequired &&
                <div style={styles.field}>
                    <TextField
                      label={FIELDS.cvv.label}
                      value={cardData.cvv}
                      onChange={e => onFieldChange(FIELDS.cvv.key, e.target.value)}
                      rightIcon={
                        <span onClick={toggleCvvVisibility} style={{ cursor: "pointer" }}>
                          {showCVV ? <EyeOnIcon /> : <EyeOffIcon />}
                        </span>}
                      onInput={(event) => ValidateNumeric(event)}
                      maxLength={3}
                      type={showCVV ? "text" : "password"}
                      onBlur={() => handleBlur(FIELDS.cvv.key)}
                      errorMessage={getErrorMsg(FIELDS.cvv)}
                    />
                </div>
              }
          </div>
        </div>
  );
};

const styles = {
  card: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  row: {
    display: "flex",
    gap: "12px",
  },
  field: {
    flex: 1,
    minWidth: 0,
  },
};

export default CardDetailsForm;
